import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  CreateContactAutomaticallyBodyType,
  CreateContactManuallyRequestType,
  CreateContactSearchExistedUserByIdRequestType,
} from '@/domains/contacts/sagas/types';
import {
  ContactEntities,
  CreateContactFieldKey,
  CreateContactFieldValue,
  CreateContactState,
} from '@/domains/contacts/store/types/createContactTypes';
import { ExistingUserData } from '@/domains/user/components/ExistingUserInfoCard/types';
import { CreateContactOptions } from '@/domains/contacts/store/enums/createContactEnums';
import { AddressFormStep } from '@/components/widgets/Stepper/components/AddressForm/enums';
import { EntityTypesEnum } from '@/enums/EntityTypesEnum';

const initialCreateContactSteps = {
  relationshipInfo: {
    relationshipId: null,
    relationshipName: '',
    relationshipType: '' as EntityTypesEnum,
    documentRecipient: '',
    reporter: '',
    assignee: '',
    claimantPortalAccess: '',
  },
  businessDetails: {
    businessName: '',
    representativeFirstName: '',
    representativeLastName: '',
    tin: '',
    documentsDeliveryPreference: '',
    documentsDeliveryPreferenceId: null,
  },
  personDetails: {
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: '',
    gender: '',
    genderId: null,
    ssn: '',
    clientId: '',
    dateOfBirth: null,
    dateOfDeath: null,
    documentsDeliveryPreference: '',
    documentsDeliveryPreferenceId: null,
    prefix: null,
    maritalStatusId: null,
    occupationId: null,
  },
  addressInfo: {
    isPaymentAddress: false,
    isResidenceAddress: false,
    isMailingAddress: false,
    reAttn: '',
    streetAddress: '',
    addressDetails: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  },
  contactInfo: {
    contactPreference: '',
    contactPreferenceId: null,
    mobileNumber: '',
    landline: '',
    email: '',
    fax: '',
  },
  paymentInfo: [],
  assignmentDetails: {
    assignmentTypeId: null,
    assignmentPercentage: null,
    assignmentAmount: null,
    minLimit: null,
    maxLimit: null,
    assignmentEffectiveDate: null,
    assignmentExpirationDate: null,
  },
  contactEntity: {
    contactHolderId: null,
    contactHolder: '',
    contactHolderName: '',
  },
};

const initialState: CreateContactState = {
  createContactType: CreateContactOptions.AUTO,
  createContactSteps: initialCreateContactSteps,
  existingCreateContactUserData: null,
  userForContactCreate: null,
  usersListForContactCreate: null,
  isUsersListForContactCreateLoading: false,
  isCreateContactExistingUserByIdLoading: false,
  isCreateContactManuallyLoading: false,
  isCreateContactAutomaticallyLoading: false,
  isCreateContactEntitiesLoading: false,
  contactEntities: [],
};

const createContactSlice = createSlice({
  name: 'createContact',
  initialState,
  reducers: {
    changeCreateContactType: (state, action: PayloadAction<CreateContactOptions>) => {
      state.createContactType = action.payload;
    },
    updateCreateContactStepValues: <K extends CreateContactFieldKey>(
      state,
      action: PayloadAction<{ step: K; value: CreateContactFieldValue<K> }>,
    ) => {
      const { step, value } = action.payload;
      state.createContactSteps[step] = value;
    },
    updateCreateContactExistingUserData: (state, action: PayloadAction<ExistingUserData | null>) => {
      state.existingCreateContactUserData = action.payload;
    },
    resetCreateContactState: (state) => ({ ...initialState, contactEntities: state.contactEntities }),
    setUserForContactCreate: (state, action: PayloadAction<ExistingUserData | null>) => {
      state.userForContactCreate = action.payload;
    },
    setUsersListForContactCreate: (state, action: PayloadAction<ExistingUserData[] | null>) => {
      state.usersListForContactCreate = action.payload;
    },
    setUsersListForContactCreateLoadingStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.isUsersListForContactCreateLoading = payload;
    },
    getCreateContactExistingUsers: (_, _action: PayloadAction<string>) => {},
    getCreateContactExistingUsersSuccess: (state, { payload }: PayloadAction<ExistingUserData[] | null>) => {
      state.usersListForContactCreate = payload;
    },
    getCreateContactExistingUserById: (_, _action: PayloadAction<CreateContactSearchExistedUserByIdRequestType>) => {},
    getCreateContactExistingUserByIdSuccess: (state, { payload }: PayloadAction<ExistingUserData | null>) => {
      state.existingCreateContactUserData = payload;
    },
    setCreateContactExistingUserByIdLoadingStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreateContactExistingUserByIdLoading = payload;
    },
    getCreateContactManually: (_, _action: PayloadAction<CreateContactManuallyRequestType>) => {},
    setCreateContactManuallyLoadingStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreateContactManuallyLoading = payload;
    },
    getCreateContactAutomatically: (_, _action: PayloadAction<CreateContactAutomaticallyBodyType>) => {},
    setCreateContactAutomaticallyLoadingStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreateContactAutomaticallyLoading = payload;
    },
    getCreateContactEntities: (_, _action: PayloadAction<number>) => {},
    getCreateContactEntitiesSuccess: (state, action: PayloadAction<ContactEntities[]>) => {
      state.contactEntities = action.payload;
    },
    setAreCreateContactEntitiesLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreateContactEntitiesLoading = payload;
    },
  },
  selectors: {
    selectCreateContactSteps: (state) => state.createContactSteps,
    selectCreateContactExistingUserData: (state) => state.existingCreateContactUserData,
    selectUserForContactCreate: (state) => state.userForContactCreate,
    selectUsersListForContactCreate: (state) => state.usersListForContactCreate,
    selectCreateContactType: (state) => state.createContactType,
    selectUsersListForContactCreateIsLoading: (state) => state.isUsersListForContactCreateLoading,
    selectCreateContactExistingUserByIdIsLoading: (state) => state.isCreateContactExistingUserByIdLoading,
    selectCreateContactManuallyIsLoading: (state) => state.isCreateContactManuallyLoading,
    selectCreateContactAutomaticallyIsLoading: (state) => state.isCreateContactAutomaticallyLoading,
    selectCreateContactAddressDetails: (state) => state.createContactSteps[AddressFormStep.AddressInfo],
    selectCreateContactEntityOptions: (state) => state.contactEntities,
    selectCreateContactEntitiesAreLoading: (state) => state.isCreateContactEntitiesLoading,
  },
});

export const {
  changeCreateContactType,
  getCreateContactExistingUserById,
  getCreateContactExistingUsers,
  resetCreateContactState,
  setUserForContactCreate,
  setUsersListForContactCreate,
  updateCreateContactExistingUserData,
  updateCreateContactStepValues,
  getCreateContactExistingUsersSuccess,
  setUsersListForContactCreateLoadingStatus,
  getCreateContactExistingUserByIdSuccess,
  setCreateContactExistingUserByIdLoadingStatus,
  getCreateContactManually,
  setCreateContactManuallyLoadingStatus,
  getCreateContactAutomatically,
  setCreateContactAutomaticallyLoadingStatus,
  getCreateContactEntities,
  getCreateContactEntitiesSuccess,
  setAreCreateContactEntitiesLoading,
} = createContactSlice.actions;

export const {
  selectCreateContactSteps,
  selectCreateContactExistingUserData,
  selectUserForContactCreate,
  selectUsersListForContactCreate,
  selectCreateContactType,
  selectUsersListForContactCreateIsLoading,
  selectCreateContactManuallyIsLoading,
  selectCreateContactAutomaticallyIsLoading,
  selectCreateContactExistingUserByIdIsLoading,
  selectCreateContactAddressDetails,
  selectCreateContactEntityOptions,
  selectCreateContactEntitiesAreLoading,
} = createContactSlice.selectors;

export const createContactReducer = createContactSlice.reducer;
export const createContactReducerPath = createContactSlice.reducerPath;
